import {Observable} from 'rxjs';
import i18next from 'i18next';
import {map} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDeleteDialogObservable, showDialogObservable} from 'web-app/react/dialog';

import * as Api from './api';
import * as Actions from './actions';

/*
    NEW VOUCHERS
*/

export const createVoucherObservable = (institutionId: string, billPayerId: string, voucher: Api.APIVoucher) => {
    return Api.createVoucher(institutionId, billPayerId, voucher).pipe(
        map(result => Actions.createPaymentSuccess.action({newPayment: result.payment, billPayerId, institutionId})),
    );
};

export const updateVoucherObservable = (
    institutionId: string,
    billPayerId: string,
    recurringPaymentId: string,
    updatedVoucher: Api.APIUpdateVoucher,
) => {
    return Api.updateVoucher(institutionId, billPayerId, recurringPaymentId, updatedVoucher).pipe(
        map(result =>
            Actions.updatePaymentSuccess.action({updatedPayment: result.updatedPayment, billPayerId, institutionId}),
        ),
    );
};

/*
    LEGACY VOUCHERS
*/

export const fetchBillPayerPaymentsObservable = (institutionId: string, billPayerId: string) => {
    return Api.fetchPayments(institutionId, billPayerId).map(data =>
        Actions.fetchBillPayerPaymentsSuccess.action({
            billPayerId,
            recurringPayments: data.recurringPayments,
            behaviors: data.behaviors,
        }),
    );
};

export const createPaymentObservable = (institutionId: string, billPayerId: string, payment: any) => {
    return Api.createPayment(institutionId, billPayerId, payment).map(result =>
        Actions.createPaymentSuccess.action({newPayment: result.payment, billPayerId, institutionId}),
    );
};

export const deletePaymentObservable = (institutionId: string, billPayerId: string, recurringPaymentId: string) =>
    Api.deletePayment(institutionId, billPayerId, recurringPaymentId).map(() =>
        Actions.deletePaymentSuccess.action({oldRecurringPaymentId: recurringPaymentId, institutionId, billPayerId}),
    );

export const denyPaymentObservable = (institutionId: string, billPayerId: string, payment: any) =>
    Api.denyPayment(institutionId, billPayerId, payment.recurringPaymentId, payment.recurrenceId).map(result =>
        Actions.denyPaymentSuccess.action({result, oldPayment: payment, institutionId, billPayerId}),
    );

const fetchBillPayerPayments = action$ =>
    action$
        .ofType(Actions.fetchBillPayerPayments.type)
        .mergeMap(({payload}) =>
            fetchBillPayerPaymentsObservable(payload.institutionId, payload.billPayerId).catch(e =>
                Observable.of(Actions.fetchBillPayerPaymentsFailed.action(e)),
            ),
        );

const addPayment = action$ =>
    action$
        .ofType(Actions.createPayment.type)
        .mergeMap(({payload}) =>
            createPaymentObservable(payload.institutionId, payload.billPayerId, payload.payment).catch(e =>
                Observable.of(Actions.createPaymentFailed.action(e)),
            ),
        );

const deletePayment = action$ =>
    action$.ofType(Actions.deletePayment.type).mergeMap(({payload}) => {
        return showDeleteDialogObservable({
            title: '',
            description: i18next.t('vouchers.confirmVoucherDelete'),
        }).mergeMap(selection => {
            if (selection === 'OK') {
                return deletePaymentObservable(
                    payload.institutionId,
                    payload.billPayerId,
                    payload.recurringPaymentId,
                ).catch(e => Observable.of(Actions.deletePaymentFailed.action(e)));
            } else {
                return Observable.of(Actions.deletePaymentAborted.action());
            }
        });
    });

const denyPayment = action$ =>
    action$.ofType(Actions.denyPayment.type).mergeMap(({payload}) => {
        return showDialogObservable({title: '', description: i18next.t('vouchers.confirmVoucherDeny')}).mergeMap(
            selection => {
                if (selection === 'OK') {
                    return denyPaymentObservable(payload.institutionId, payload.billPayerId, payload.payment).catch(e =>
                        Observable.of(Actions.denyPaymentFailed.action(e)),
                    );
                } else {
                    return Observable.of(Actions.denyPaymentAborted.action());
                }
            },
        );
    });

export default combineEpics(fetchBillPayerPayments, addPayment, deletePayment, denyPayment);
